/**
 * Logic Set Dynamic Text Generator
 * PBI 266671 - Generates dynamic explanatory text for Logic Set elements
 *
 * Based on the text construction flowchart from Figma Documentation 18:
 * 1. Determine condition type (Carry-Timer, Carry-LogicSet, Non-carry)
 * 2. Apply edge trigger text (None, Rising, Falling, Both)
 * 3. Apply hysteresis text if enabled
 */

import strings from 'utils/strings'
import { timerValue, timerValueFull } from 'utils/timers'
import { opMap } from 'components/utils'
import { CARRY_LOGIC_SET, CARRY_TIMER, SELF_REFERENCE } from 'utils/constants'

/**
 * Edge trigger types
 */
export const EDGE_TRIGGER = {
  NONE: 'none',
  RISING: 'rising',
  FALLING: 'falling',
  BOTH: 'both',
}

/**
 * Logic gate operators
 */
export const LOGIC_OPERATORS = {
  AND: 'and',
  OR: 'or',
  XOR: 'xor',
  TOGGLE: 'toggle',
}

/**
 * Generates the edge trigger text suffix based on the edge trigger type
 * @param {string} edgeTriggerType - 'none', 'rising', 'falling', or 'both'
 * @returns {string} - Edge trigger text suffix or empty string
 */
export const getEdgeTriggerText = (edgeTriggerType) => {
  switch (edgeTriggerType) {
    case EDGE_TRIGGER.RISING:
      return ` ${strings.condition_transitions_rising}`
    case EDGE_TRIGGER.FALLING:
      return ` ${strings.condition_transitions_falling}`
    case EDGE_TRIGGER.BOTH:
      return ` ${strings.condition_transitions_both}`
    case EDGE_TRIGGER.NONE:
    default:
      return ''
  }
}

/**
 * Map of operators to their complementary operators for hysteresis
 */
const complementaryOpMap = {
  '<': '≥',
  '≤': '>',
  '>': '≤',
  '≥': '<',
  5: '≥', // LESS_THAN → GREATER_THAN_OR_EQUAL
  7: '>', // LESS_THAN_OR_EQUAL → GREATER_THAN
  4: '≤', // GREATER_THAN → LESS_THAN_OR_EQUAL
  6: '<', // GREATER_THAN_OR_EQUAL → LESS_THAN
}

/**
 * Determines if hysteresis should be added or subtracted from value
 * For < and ≤ operators: hysteresis is ADDED
 * For > and ≥ operators: hysteresis is SUBTRACTED
 */
const shouldAddHysteresis = (operator) => {
  const addOps = ['<', '≤', 5, 7]
  return addOps.includes(operator)
}

/**
 * Generates the hysteresis text suffix if hysteresis is enabled
 * @param {Object} params - Hysteresis parameters
 * @param {number|null} params.hysteresisValue - The hysteresis value
 * @param {string} params.input - The input/signal description
 * @param {string|number} params.operator - The condition operator
 * @param {number} params.value - The condition value
 * @param {string} params.unit - The condition unit (e.g. 'V', 'MPH')
 * @returns {string} - Hysteresis text suffix or empty string
 */
export const getHysteresisText = ({
  hysteresisValue,
  input,
  operator,
  value,
  unit,
}) => {
  if (hysteresisValue === null || hysteresisValue === undefined) {
    return ''
  }

  const displayOp = opMap[operator] || operator
  const complementaryOp =
    complementaryOpMap[operator] || complementaryOpMap[displayOp]
  if (!complementaryOp) {
    return ''
  }

  const numValue = Number(value) || 0
  const numHysteresis = Number(hysteresisValue) || 0
  const releaseValue = shouldAddHysteresis(operator)
    ? numValue + numHysteresis
    : numValue - numHysteresis

  return ` ${strings.condition_with_hysteresis(
    input,
    complementaryOp,
    releaseValue,
    unit || ''
  )}`
}

/**
 * Generates condition text based on condition data
 * Implements the decision tree from Figma Documentation 18
 *
 * @param {Object} condition - The condition data
 * @param {string} condition.rungType - Type of condition (CARRY_LOGIC_SET, CARRY_TIMER, etc.)
 * @param {string} condition.selectedInputDescription - The input/signal description
 * @param {string} condition.operator - The comparison operator
 * @param {*} condition.value - The comparison value
 * @param {string} condition.edgeTriggerType - Edge trigger type
 * @param {number|null} condition.hysteresis - Hysteresis value
 * @param {string} condition.carrySourceDescription - Description for carry references
 * @returns {string} - The generated condition explanation text
 */
export const generateConditionText = (condition) => {
  const {
    rungType,
    selectedInputDescription,
    operator,
    value,
    edgeTriggerType = EDGE_TRIGGER.NONE,
    hysteresis = null,
    carrySourceDescription,
  } = condition

  let baseText = ''

  // Step 1: Determine base text based on condition type
  if (rungType === CARRY_LOGIC_SET || rungType === SELF_REFERENCE) {
    // Carry Logic Set - use the value setting (TRUE/FALSE)
    const carryValue =
      value === 1 || value === '1' || value === true ? 'TRUE' : 'FALSE'
    baseText = strings.carry_logic_set_is_true(
      carrySourceDescription || selectedInputDescription,
      carryValue
    )
  } else if (rungType === CARRY_TIMER) {
    // Carry Timer
    const formattedValue = timerValue(value)
    baseText = strings.carry_timer_is_true(
      selectedInputDescription,
      opMap[operator] || operator,
      formattedValue
    )
  } else {
    // Non-carry condition
    baseText = strings.condition_is_true_when(
      selectedInputDescription,
      opMap[operator] || operator,
      value
    )
  }

  // Step 2: Apply edge trigger text
  const edgeText = getEdgeTriggerText(edgeTriggerType)

  // Step 3: Apply hysteresis text
  const hysteresisText = getHysteresisText({
    hysteresisValue: hysteresis,
    input: selectedInputDescription,
    operator,
    value,
    unit: condition.units,
  })

  return `${baseText}${edgeText}${hysteresisText}`
}

/**
 * Generates logic gate explanation text
 *
 * @param {string} operator - The logic gate operator ('and', 'or', 'xor')
 * @param {string} elementA - Description of the first input element
 * @param {string} elementB - Description of the second input element
 * @returns {string} - The generated logic gate explanation text
 */
export const generateLogicGateText = (operator, elementA, elementB) => {
  switch (operator?.toLowerCase()) {
    case LOGIC_OPERATORS.AND:
      return strings.logic_gate_and_text(elementA, elementB)
    case LOGIC_OPERATORS.OR:
      return strings.logic_gate_or_text(elementA, elementB)
    case LOGIC_OPERATORS.XOR:
      return strings.logic_gate_xor_text(elementA, elementB)
    case LOGIC_OPERATORS.TOGGLE:
      return strings.logic_gate_toggle_text(elementA, elementB)
    default:
      return strings.logic_gate_or_text(elementA, elementB)
  }
}

/**
 * Generates activate delay explanation text
 *
 * @param {number} delayValue - The delay timer value in milliseconds
 * @returns {string} - The generated delay explanation text
 */
export const generateActivateDelayText = (delayValue) => {
  const formattedValue = timerValueFull(delayValue)
  return strings.activate_delay_text(formattedValue)
}

/**
 * Generates deactivate delay explanation text
 *
 * @param {number} delayValue - The delay timer value in milliseconds
 * @returns {string} - The generated delay explanation text
 */
export const generateDeactivateDelayText = (delayValue) => {
  const formattedValue = timerValueFull(delayValue)
  return strings.deactivate_delay_text(formattedValue)
}

/**
 * Determines which elements are active in a Logic Set
 * Returns the list of tiles to display in the right panel
 *
 * @param {Object} logicSet - The Logic Set data
 * @param {Array} logicSet.conditions - Array of condition URIs
 * @param {number} logicSet.delayActivate - Activate delay value
 * @param {number} logicSet.delayDeactivate - Deactivate delay value
 * @returns {Array} - Array of active element identifiers in display order
 */
export const getActiveElements = (logicSet) => {
  const { conditions = [], delayActivate = 0, delayDeactivate = 0 } = logicSet

  const activeElements = []

  // Fixed order per Figma Documentation 17:
  // 1. Condition 1
  // 2. Condition 2
  // 3. Logic Gate - Top
  // 4. Condition 3
  // 5. Condition 4
  // 6. Logic Gate - Bottom
  // 7. Logic Gate - Middle
  // 8. Activate Delay
  // 9. Deactivate Delay

  if (conditions[0]) {
    activeElements.push({
      type: 'condition',
      index: 0,
      label: strings.condition_number_label(1),
    })
  }
  if (conditions[1]) {
    activeElements.push({
      type: 'condition',
      index: 1,
      label: strings.condition_number_label(2),
    })
  }
  // Logic Gate - Top is shown if conditions 1 AND 2 exist
  if (conditions[0] && conditions[1]) {
    activeElements.push({
      type: 'logicGate',
      position: 'top',
      label: strings.logic_gate_top,
    })
  }
  if (conditions[2]) {
    activeElements.push({
      type: 'condition',
      index: 2,
      label: strings.condition_number_label(3),
    })
  }
  if (conditions[3]) {
    activeElements.push({
      type: 'condition',
      index: 3,
      label: strings.condition_number_label(4),
    })
  }
  // Logic Gate - Bottom is shown if conditions 3 AND 4 exist
  if (conditions[2] && conditions[3]) {
    activeElements.push({
      type: 'logicGate',
      position: 'bottom',
      label: strings.logic_gate_bottom,
    })
  }
  // Logic Gate - Middle is shown if there are multiple conditions
  if (conditions.length > 1) {
    activeElements.push({
      type: 'logicGate',
      position: 'middle',
      label: strings.logic_gate_middle,
    })
  }
  if (delayActivate > 0) {
    activeElements.push({
      type: 'delay',
      delayType: 'activate',
      label: strings.activate_delay_label,
    })
  }
  if (delayDeactivate > 0) {
    activeElements.push({
      type: 'delay',
      delayType: 'deactivate',
      label: strings.deactivate_delay_label,
    })
  }

  return activeElements
}

export default {
  generateConditionText,
  generateLogicGateText,
  generateActivateDelayText,
  generateDeactivateDelayText,
  getActiveElements,
  getEdgeTriggerText,
  getHysteresisText,
  EDGE_TRIGGER,
  LOGIC_OPERATORS,
}
